import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import {CONTENT_RESTRICTIONS_PHASE_2_FLAG} from 'client/components/LockAuthorization';
import type {ClassworkCreatedByFilter, SortValue} from 'client/TeacherDashboard/types';
import type {AssignmentFilterInput, AssignmentSortInput} from 'gql/types';
import {AssignmentSortField, AssignmentStatus, SortOrder} from 'gql/types';
import {normalizeError} from 'lib/normalizeError';

import type {StatusSegment} from '../constants';
import type {TeacherAssignmentRowFieldsFragment} from '../queries/TeacherAssignments.queries.generated';
import {useTeacherAssignmentsQuery} from '../queries/TeacherAssignments.queries.generated';

const PAGE_SIZE = 25;

export type AssignmentSortValue = 'recentlyEdited' | 'name';

const STATUS_ENUM: Record<StatusSegment, AssignmentStatus> = {
  open: AssignmentStatus.Open,
  closed: AssignmentStatus.Closed,
  scheduled: AssignmentStatus.Scheduled,
  draft: AssignmentStatus.Draft
};

export interface TeacherAssignmentsFilters {
  search: string;
  segment: StatusSegment;
  createdBy: ClassworkCreatedByFilter;
  /** The acting (possibly masqueraded) teacher id, for the created-by filter. */
  actorTeacherId: string;
  sort: SortValue<AssignmentSortValue>;
  /** Scopes the per-educator status/date calcs to one class; omit for cross-class. */
  classroomId?: string;
}

const buildAssignmentFilter = ({
  search,
  segment,
  createdBy,
  actorTeacherId,
  classroomId
}: Omit<TeacherAssignmentsFilters, 'sort'>): AssignmentFilterInput => {
  const classroomIds = classroomId ? [classroomId] : undefined;
  const filter: AssignmentFilterInput = {
    educatorStatus: {eq: STATUS_ENUM[segment], input: classroomIds && {classroomIds}}
  };
  if (search) {
    filter.name = {ilike: `%${search}%`};
  }
  if (createdBy === 'you') {
    filter.teacherId = {eq: actorTeacherId};
  } else if (createdBy === 'someoneElse') {
    filter.teacherId = {notEq: actorTeacherId};
  }
  if (classroomIds) {
    filter.assignedToClassrooms = {eq: true, input: {classroomIds}};
  }
  return filter;
};

const buildAssignmentSort = ({
  field,
  order
}: SortValue<AssignmentSortValue>): AssignmentSortInput[] => {
  const sortOrder = order === 'desc' ? SortOrder.Desc : SortOrder.Asc;
  if (field === 'name') {
    return [{field: AssignmentSortField.Name, order: sortOrder}];
  }
  // Recently edited; Name asc as a stable tiebreak.
  return [
    {field: AssignmentSortField.UpdatedAt, order: sortOrder},
    {field: AssignmentSortField.Name, order: SortOrder.Asc}
  ];
};

interface UseTeacherAssignmentsResult {
  assignments: TeacherAssignmentRowFieldsFragment[];
  isLoading: boolean;
  hasNextPage: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  /** Reset pagination and re-pull page one (e.g. after a delete). */
  refetch: () => void;
}

export const useTeacherAssignments = (
  filters: TeacherAssignmentsFilters
): UseTeacherAssignmentsResult => {
  const {search, segment, createdBy, actorTeacherId, classroomId} = filters;
  const {field: sortField, order: sortOrder} = filters.sort;

  // Drives the @include on resolvedLockAuthorization so the chip's resolved calc only runs for the
  // content-restrictions phase-2 experience, not for every teacher's assignment list.
  const {isFeatureEnabled: adminManagingRestrictions} = useIsFeatureFlagEnabled(
    CONTENT_RESTRICTIONS_PHASE_2_FLAG
  );

  // Keyed on primitives, not the fresh-per-render filters object, to keep loadMore/query stable.
  const variables = useMemo(
    () => ({
      classroomIds: classroomId ? [classroomId] : undefined,
      filter: buildAssignmentFilter({search, segment, createdBy, actorTeacherId, classroomId}),
      sort: buildAssignmentSort({field: sortField, order: sortOrder}),
      first: PAGE_SIZE,
      includeResolvedLockAuth: Boolean(adminManagingRestrictions)
    }),
    [
      search,
      segment,
      createdBy,
      actorTeacherId,
      classroomId,
      sortField,
      sortOrder,
      adminManagingRestrictions
    ]
  );

  // Keyset pages beyond the first are appended in local state (the field is
  // arg-keyed, so Apollo can't merge pages itself without a typePolicy).
  const [moreResults, setMoreResults] = useState<TeacherAssignmentRowFieldsFragment[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // endKeyset of the last page = the `after` cursor for the next fetch.
  const nextCursorRef = useRef<string | undefined>(undefined);
  // Drops stale fetchMore responses.
  const requestGenRef = useRef(0);

  const {
    data,
    loading,
    error,
    fetchMore,
    refetch: apolloRefetch
  } = useTeacherAssignmentsQuery({fetchPolicy: 'cache-and-network', variables});

  const resetPages = useCallback(() => {
    setMoreResults([]);
    nextCursorRef.current = undefined;
    requestGenRef.current += 1;
  }, []);

  const prevVariablesRef = useRef(variables);
  if (prevVariablesRef.current !== variables) {
    prevVariablesRef.current = variables;
    resetPages();
  }

  useEffect(() => {
    if (error) {
      addToast({
        color: 'negative',
        title: "Couldn't fetch assignments",
        message: normalizeError(error).message
      });
    }
  }, [error]);

  const page = data?.assignmentsAsEducator;
  const assignments = useMemo(() => {
    const seen = new Set<string>();
    const merged: TeacherAssignmentRowFieldsFragment[] = [];
    for (const assignment of [...(page?.results ?? []), ...moreResults]) {
      if (!seen.has(assignment.id)) {
        seen.add(assignment.id);
        merged.push(assignment);
      }
    }
    return merged;
  }, [page?.results, moreResults]);

  const nextCursor = nextCursorRef.current ?? page?.endKeyset ?? null;
  const fetchedCount = (page?.results?.length ?? 0) + moreResults.length;
  const hasNextPage = Boolean(nextCursor) && fetchedCount < (page?.count ?? 0);

  const loadMore = useCallback(() => {
    if (loading || isLoadingMore || !hasNextPage || !nextCursor) {
      return;
    }
    const generation = requestGenRef.current;
    setIsLoadingMore(true);
    fetchMore({variables: {...variables, after: nextCursor}})
      .then((result) => {
        if (generation !== requestGenRef.current) {
          return;
        }
        const nextPage = result.data?.assignmentsAsEducator;
        setMoreResults((current) => [...current, ...(nextPage?.results ?? [])]);
        nextCursorRef.current = nextPage?.endKeyset ?? undefined;
      })
      .catch((fetchMoreError) => {
        if (generation !== requestGenRef.current) {
          return;
        }
        addToast({
          color: 'negative',
          title: "Couldn't load more assignments",
          message: normalizeError(fetchMoreError).message
        });
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [fetchMore, hasNextPage, isLoadingMore, loading, nextCursor, variables]);

  const refetch = useCallback(() => {
    resetPages();
    apolloRefetch();
  }, [apolloRefetch, resetPages]);

  return {
    assignments,
    isLoading: loading && !data,
    hasNextPage,
    isLoadingMore,
    loadMore,
    refetch
  };
};
